import React, { useState } from 'react';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { CancelSessionBookedModal } from '../../../CancelSessionBookedModal';
import { FormattedCoachingVisit } from '../../../../../models/Sessions';
import { getCurrentUser } from '../../../../../selectors/userSelectors';
import ReportVisitDialog from '../../../../Dialogs/ReportVisit';
import SessionState from './SessionState/SessionState';
import { colors } from '../../../../../themes/colors';
import CompanyName from './CompanyName/CompanyName';
import MemberName from './MemberName/MemberName';
import MemberSessionTime from './MemberSessionTime/MemberSessionTime';
import ProviderSessionTime from './ProviderSessionTime/ProviderSessionTime';
import SessionActions from './SessionActions/SessionActions';
import { SessionRowTestID } from './constants';
import { useCancelEventMutation } from './documents/CancelEventMutation.generated';
import SessionType from './SessionType/SessionType';

const styles = (theme) =>
  createStyles({
    sessionContainer: {
      minHeight: 75,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 3fr',
      borderTop: `1px solid ${colors.gray12}`,
      justifyContent: 'flex-start',
      alignItems: 'center',
      transition: theme.transitions.create(['background-color']),
      cursor: 'pointer',

      '&:hover': {
        backgroundColor: colors.gray19,

        '& [class*="SessionRow-memberName"]': {
          color: colors.blue11,
          textDecoration: 'underline',
        },
      },
    },
    sessionContainerWithSessionType: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 3fr',
    },
  });

export interface SessionRowProps {
  isUpcomingView: boolean;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
  onJoinSession: () => void;
  onSuccessfulCancel: VoidFunction;
  onUpdateVisit?: () => void;
  onVisibleMemberProfileDrawer: (Member) => void;
  visit: FormattedCoachingVisit;
}

type Props = WithStyles<typeof styles> & SessionRowProps;

const SessionRow: React.FC<Props> = ({
  classes,
  isUpcomingView,
  memberClient,
  onJoinSession,
  onSuccessfulCancel,
  onUpdateVisit,
  onVisibleMemberProfileDrawer,
  visit,
}) => {
  const currentUser = useSelector(getCurrentUser);
  const { showHpiClientStatusInfo } = useFlags();
  const [reportVisitModalOpen, setReportVisitModalOpen] = useState(false);
  const [showCancelSessionBookedModal, setShowCancelSessionBookedModal] = useState(false);
  const providerIsPsychiatrist = currentUser.isPsychiatrist;
  const snackbar = useSnackbar();

  const startTime = visit.scheduledDateTime.setZone(currentUser.localTimezone).toFormat('h:mm a'); // ('h:mm A');
  const endTime = visit.scheduledDateTime
    .plus({ minutes: visit.scheduledDuration })
    .setZone(currentUser.localTimezone)
    .toFormat('h:mm a');

  const [cancelEventMutation] = useCancelEventMutation({
    client: memberClient,
    onCompleted: (data) => {
      if (data?.cancelEvent?.ok) {
        onSuccessfulCancel();
        snackbar.enqueueSnackbar('Event canceled', { variant: 'success' });
      } else {
        snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
      }
    },
    onError: () => {
      snackbar.enqueueSnackbar('Could not cancel event', { variant: 'error' });
    },
  });

  const cancelVisit = async () => {
    if (!visit) {
      return;
    }

    await cancelEventMutation({
      variables: { uuid: visit.uuid },
    });

    setShowCancelSessionBookedModal(false);
  };

  const toggleMemberProfileDrawer = (e) => {
    // check if the clicked target is maximally a second degree child of the row
    // this prevents the member profile drawer from being opened/closed when user clicks on any button inside the row
    // that is intended for triggering a different action
    const matchesChildren = [...e.currentTarget.children].filter(
      (item) =>
        item === e.target ||
        [...item.children].filter((childItem) => childItem === e.target).length,
    );

    if (e.target === e.currentTarget || matchesChildren.length) {
      onVisibleMemberProfileDrawer(visit.coachAssignment.member);
    }
  };

  const sessionRowClassName = classNames(classes.sessionContainer, {
    [classes.sessionContainerWithSessionType]: showHpiClientStatusInfo,
  });

  return (
    <div
      className={sessionRowClassName}
      data-testid={SessionRowTestID.MemberProfileButton}
      onClick={(e) => toggleMemberProfileDrawer(e)}
    >
      <ProviderSessionTime endTime={endTime} startTime={startTime} />

      <MemberSessionTime visit={visit} />

      <SessionState state={visit.state} />

      <MemberName member={visit.coachAssignment.member} />

      <SessionType
        showHpiClientStatusInfo={showHpiClientStatusInfo}
        providerIsPsychiatrist={providerIsPsychiatrist}
        visit={visit}
      />

      <CompanyName company={visit.coachAssignment.member.company} />

      <SessionActions
        currentUser={currentUser}
        isUpcomingView={isUpcomingView}
        onJoinSession={onJoinSession}
        setReportVisitModalOpen={setReportVisitModalOpen}
        setShowCancelSessionBookedModal={setShowCancelSessionBookedModal}
        visit={visit}
      />

      {reportVisitModalOpen && (
        <ReportVisitDialog
          coachAssignment={visit.coachAssignment}
          onCancel={() => setReportVisitModalOpen(false)}
          onSuccess={() => setReportVisitModalOpen(false)}
          onUpdateVisit={onUpdateVisit}
          showSuccessModal
          visit={visit}
        />
      )}

      <CancelSessionBookedModal
        closeDialog={() => setShowCancelSessionBookedModal(false)}
        isOpen={showCancelSessionBookedModal}
        memberName={`${visit.coachAssignment.member.firstName} ${visit.coachAssignment.member.lastName}`}
        onCancel={cancelVisit}
        onKeepSession={() => setShowCancelSessionBookedModal(false)}
        sessionDate={`${startTime} - ${endTime}`}
      />
    </div>
  );
};

export default withStyles(styles)(SessionRow);
